// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { AppType, ExperienceTypes, UrlSearchParamsHelper, WebWorkerMessageId, environment, logger, perfMarker, sendMessage, webWorkerCache } from "../wwCommon";
import { aadTokenQsKey, aadTokenQsVal, getOneServiceFeedCallHeaders } from "./OneServiceRequest";
import { subscribeToExperienceConfig, tryReformatGuid, trySetSearchParam } from "./Common";
import { FetchIndustryNewsArticlesFailed } from "../AppErrors";
import { get } from "lodash-es";
import { getExperienceTypeConfig } from "../wwResolver";
import { oneServiceQueryStringKeys } from "./OneServiceConstants";
import { registerDataMessageHandler } from "../wwCommon";
/** Makes fetch call for Industry News Articles and sets the response in the web worker cache */
export function registerFetchIndustryNewsArticles() {
    if (!isFeatureEnabled()) {
        return;
    }
    registerDataMessageHandler(WebWorkerMessageId.IndustryNewsArticles, () => {
        // Subscribe to the web worker cache, which returns immediately if the response already exists or is added
        const industryNewsArticlesSubscription = webWorkerCache.subscribe(WebWorkerMessageId.IndustryNewsArticles, industryNewsArticlesResponse => {
            industryNewsArticlesSubscription.unsubscribe();
            sendMessage({ id: WebWorkerMessageId.IndustryNewsArticles, fetched: true, payload: industryNewsArticlesResponse.payload });
        });
    });
    // wait for the auth token to be fetched
    const subscription = webWorkerCache.subscribe(WebWorkerMessageId.AuthToken, (data) => {
        subscription.unsubscribe();
        // wait for the Industry News Card configs to be loaded
        // the OCID value from the config is required to make the article fetch call
        subscribeToExperienceConfig(ExperienceTypes.IndustryNewsCard).then(() => {
            fetchIndustryNewsArticles();
        });
    });
}
/** Fetches the industry news articles */
function fetchIndustryNewsArticles() {
    return __awaiter(this, void 0, void 0, function* () {
        perfMarker(`${WebWorkerMessageId.IndustryNewsArticles}FetchStart`);
        const url = getUrl();
        if (!url) {
            onFetchComplete();
            return;
        }
        const init = {
            method: "GET",
            headers: getOneServiceFeedCallHeaders(true /* useEnterpriseHeaders */)
        };
        return fetch(url.toString(), init).then(response => {
            if (!response.ok) {
                throw Error(`fetchIndustryNewsArticles call failed: Status: ${response.status} and StatusText: ${response.statusText}`);
            }
            // Handle No-Content response
            if (response.status === 204) {
                // return an empty response according to the response schema
                return { subCards: [] };
            }
            return response.json();
        }).then(data => {
            if (!Array.isArray(data.subCards) || data.subCards.length === 0) {
                logger.error(Object.assign(Object.assign({}, FetchIndustryNewsArticlesFailed), { message: "WW Industry News Article fetch response was empty" }));
            }
            onFetchComplete(data);
        }).catch(reason => {
            logger.error(Object.assign(Object.assign({}, FetchIndustryNewsArticlesFailed), { message: "WW Industry News Article fetch failed", pb: Object.assign(Object.assign({}, FetchIndustryNewsArticlesFailed.pb), { customMessage: `Error: ${reason} for Url: ${url}` }) }));
            onFetchComplete();
        });
    });
}
/** Generates the URL used to fetch the industry news articles */
function getUrl() {
    const requestPath = "MSN/Feed/me";
    const urlBase = environment.enterpriseServiceUrlBase;
    const url = new URL(requestPath, urlBase);
    const urlSearchParams = new UrlSearchParamsHelper(urlBase.search);
    setDefaultSearchParams(urlSearchParams);
    url.search = urlSearchParams.toString();
    return url;
}
/** Sets the default QSPs for the industry news articles fetch */
function setDefaultSearchParams(urlSearchParams) {
    // get the ocid from the industry news card configs rather than based on page type
    const industryNewsConfig = getExperienceTypeConfig(ExperienceTypes.IndustryNewsCard);
    const industryNewsOcid = get(industryNewsConfig, "properties.requestOptions.ocid");
    urlSearchParams.set(oneServiceQueryStringKeys.apiKey, environment.oneServiceApiKey)
        .set(oneServiceQueryStringKeys.queryType, "myfeed")
        .set(oneServiceQueryStringKeys.ocid, industryNewsOcid || "Peregrine")
        .set(oneServiceQueryStringKeys.market, environment.locale)
        .set(oneServiceQueryStringKeys.responseSchema, "cardview")
        .set(oneServiceQueryStringKeys.wrapOData, "false")
        .set(oneServiceQueryStringKeys.disableTypeSerialization, "true")
        .set(oneServiceQueryStringKeys.timeOut, "3000")
        .set(oneServiceQueryStringKeys.top, "4")
        .set(oneServiceQueryStringKeys.query, "worknews")
        .set(oneServiceQueryStringKeys.contentType, "article,video,slideshow,webcontent")
        .set(aadTokenQsKey, aadTokenQsVal);
    if (environment.latitude && environment.longitude) {
        urlSearchParams.set(oneServiceQueryStringKeys.location, `${environment.latitude}|${environment.longitude}`);
    }
    if (!environment.sendFeedCallActivityIdInHeader) {
        trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.activityId, tryReformatGuid(environment.activityId));
    }
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.user, environment.userId);
    trySetSearchParam(urlSearchParams, oneServiceQueryStringKeys.fdhead, environment.currentFlights);
    urlSearchParams.set(oneServiceQueryStringKeys.user, environment.userId);
}
/** Marks cache state complete for industry news articles fetch. Responds to any pending message for the industry news articles. */
function onFetchComplete(responseData) {
    perfMarker(`${WebWorkerMessageId.IndustryNewsArticles}FetchEnd`);
    logger.info(`WebWorker.fetchIndustryNewsArticlesComplete: [id ${WebWorkerMessageId.IndustryNewsArticles}]`);
    webWorkerCache.set(WebWorkerMessageId.IndustryNewsArticles, { payload: { responseData } });
}
/** Checks if IndustryNewsCard feature is enabled */
function isFeatureEnabled() {
    // Enable the call only the Enterprise blended page
    if (!environment.isEnterpriseCompliantNTP) {
        return false;
    }
    // Enable this fetch only on EdgeChromium
    return environment.apptype === AppType.EdgeChromium;
}
//# sourceMappingURL=IndustryNewsArticles.js.map